import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import {
  //AdminPageHeader,
  ButtonPropsTypes,
  ButtonStyles,
  ButtonSize,
  // Card,
  // HeaderStyle,
  useBreakpoint,
  Button,
  Input,
  InputTypes,
  InputValidations,
  MLPVersion,
  Modal,
  PhoneNumber,
  ProfileImage,
  Theme,
  DropDown,
  DropDownValidations,
} from '../../../ui/components';
import { LoggerService, processProfilePictureData } from '../../../ui/utils';
import { customEventPropType } from '../../../ui/components/ImageEditor/types/propsType';
import RemoveButton from '../../../assets/image/icons/remove_button.svg';
import ChangeButton from '../../../assets/image/icons/change_button.svg';
import ProfilePhotoUpload from '../../../assets/image/ProfileImageUpload.svg';
import userService from '../../../sdk/services/userService';
import type { DynamicObjectInterface } from '../../../sdk/type';
import ImageEditor from '../../../ui/components/ImageEditor/ImageEditor';
import { formatCampaignId, isValidEmail } from '../../../ui/utils/common';
import { useAPICall } from '../../../hooks';
import AttachmentService from '../../../sdk/services/attachmentService';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { countryCodeNumber } from '../../../__mockData/phoneNumber';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import styles from './index.module.scss';

interface UserData {
  userId: string;
  password: string;
  email: string;
  phone: string;
  loginId: string;
  frameworkRole: string;
  memberType: string;
  isPrimary: string;
  name: string;
  gender: string;
  status: string;
  address: string;
  state: string;
  occupation: string;
  incomeRange: string;

  [key: string]: string | undefined;
}

export const EditPersonalDetails = (props: { decodedData: DynamicObjectInterface }) => {
  const [urlParamValue, setUrlParamValue] = useState({
    campaignId: '',
    teamId: '',
    userId: '',
  });

  const [data, setData] = useState<UserData | null>(null);
  const [showProfileUploadModal, setShowModal] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [dropdownValue, setDropdownValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneValid, setPhoneValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);
  const [isProfileImageDeleted, setIsProfileImageDeleted] = useState(false);
  // const [showHead, setShowHead] = useState(false);
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const [gender, setGender] = useState('');
  const apiCall = useAPICall();
  const location = useLocation();
  const navigate = useNavigate();
  const { adminToast } = useAdminToast();
  const [clickedFields, setClickedFields] = useState({
    loginId: false,
    password: false,
    name: false,
    frameworkRole: false,
    memberType: false,
    email: false,
    phone: false,
    address: false,
    state: false,
    occupation: false,
    incomeRange: false,
  });

  useEffect(() => {
    if (data?.phone) {
      const [countryCode, number] = data.phone.split(' ');
      setDropdownValue(countryCode);
      setPhoneValue(number);
      if (number) {
        setPhoneValid(!(number.length > 0 && (number.length < 8 || number.length > 10)));
      } else {
        setPhoneValid(false);
      }
    }

    if (data?.email) {
      setEmailValid(isValidEmail(data.email));
    }
  }, [data]);

  useEffect(() => {
    setUrlParamValue(prev => {
      const params = new URLSearchParams(location.search);
      const userId = params.get('id') ?? '';
      prev.userId = userId;
      return { ...prev };
    });
    fetchData();
    fetchProfileImage();
  }, [location]);

  const fetchData = async () => {
    const res = await apiCall(userService.getUserDetails, urlParamValue.userId);
    if (res.data.data) {
      let _data = res.data.data;
      setData(_data);
    }
  };

  const fetchProfileImage = async () => {
    try {
      const response = await AttachmentService.downloadProfileImage(urlParamValue.userId.toString() ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrl(response.data.data.image);
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  useEffect(() => {
    if (data) {
      const { phone } = data;
      if (phone) {
        const [countryCode, number] = phone.split(' ');
        setDropdownValue(countryCode);
        setPhoneValue(number);
      }
      //need change
      setGender(gender);
    }
  }, [data]);

  const deleteProfile = () => {
    setProfileImageUrl('');
    setIsProfileImageDeleted(true);
  };

  const handleFileImage = (e: customEventPropType, view: string) => {
    const file = e.files[0];
    setProfileImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setProfileImageUrl(objectUrl);
    setShowModal(false);
    setIsProfileImageDeleted(false);
  };

  const handleFileUpload = async () => {
    try {
      if (profileImageFile && urlParamValue?.userId) {
        const formData = processProfilePictureData(profileImageFile, urlParamValue.userId);
        const response = await apiCall(() => AttachmentService.postUploadProfile(formData));
        if (!response || response.status !== 200) {
          adminToast({
            message: 'Failed to upload profile image.',
            theme: Theme.ERROR,
          });
        }
      }
    } catch (error) {
      LoggerService.log('Error uploading file', error);
    }
  };

  const handleFileDelete = async () => {
    try {
      if (isProfileImageDeleted && urlParamValue?.userId) {
        const payload = {
          loginId: urlParamValue?.userId,
        };
        await apiCall(AttachmentService.removeProfile, payload);
      }
    } catch (error) {
      LoggerService.log('Error uploading file', error);
    }
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0] as File | null;

    setProfileImageFile(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setProfileImageUrl(objectUrl);
    } else {
      setProfileImageUrl('');
    }
    setShowModal(false);
    setIsProfileImageDeleted(false);
  };

  const uploadImageModal = (
    <>
      {showProfileUploadModal && (
        <Modal
          primaryButtonLabel={''}
          headline={'Upload Profile Picture'}
          containerWidth={'550px'}
          containerHeight={'570px'}
          closeIcon={true}
          onClose={() => setShowModal(false)}
        >
          <div>
            {profileImageUrl ? (
              <div className={styles['profile-image-container']}>
                <div className={styles['image-wrapper']}>
                  <img
                    src={profileImageUrl}
                    alt="Before Front View"
                    className={styles['profile-image']}
                    style={{ borderRadius: '50%' }}
                  />
                </div>
                <div className={styles['buttons-container']}>
                  <button
                    className={styles['change-photo']}
                    onClick={() => document.getElementById('profileImageUrl')?.click()}
                  >
                    <img src={ChangeButton} alt="bg" />
                    <input
                      type="file"
                      id="profileImageUrl"
                      accept="image/*"
                      onChange={e => handleFile(e, 'profileImageUrl')}
                      style={{ display: 'none' }}
                    />
                  </button>
                  <button className={styles['remove-photo']} onClick={() => deleteProfile()}>
                    <img src={RemoveButton} alt="bg" />
                  </button>
                </div>
              </div>
            ) : (
              <ImageEditor onFileUpload={e => handleFileImage(e, 'file')} accept="image/*" />
            )}
          </div>
        </Modal>
      )}
    </>
  );

  const saveChanges = async () => {
    try {
      // Example: Update API call
      if (!isValidateInputs()) {
        adminToast({
          message: 'Please enter the valid information and all mandatory field.',
          theme: Theme.ERROR,
        });
        return;
      }
      const response = await apiCall(userService.updateUserProfile, `${urlParamValue.userId}`, data);
      if (response.status === 200) {
        navigate(`/profile?id=` + urlParamValue.userId);
        window.location.reload();
      } else {
        // Handle error
      }
    } catch (error) {
      LoggerService.log('Error updating participant details', error);
    }
  };

  const isValidateInputs = (): boolean => {
    let isValid = false;
    if (
      emailValid &&
      phoneValid &&
      phoneValue &&
      data?.name !== '' &&
      data?.gender !== null &&
      data?.gender !== '' &&
      data?.race !== null &&
      data?.race !== ''
    ) {
      isValid = true;
    }
    return isValid;
  };

  const buttonConfig: ButtonPropsTypes[] = [
    {
      title: t('admin.userEdit.cancel'),
      buttonStyle: ButtonStyles.OUTLINE,
      onClick: () => {
        navigate(`/profile?id=` + urlParamValue.userId);
      },
    },
    {
      title: t('admin.userEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: !isValidateInputs(),
      onClick: () => {
        if (profileImageUrl) {
          handleFileUpload();
        } else {
          handleFileDelete();
        }
        if (formatCampaignId(urlParamValue.userId ?? '')) {
          saveChanges();
        } else {
          adminToast({
            message: 'Only letters (A-z) and numbers with no spaces are allowed to be set in Login ID',
            theme: Theme.ERROR,
          });
        }
      },
    },
  ];

  const handleInputChange = (name: string, value: DynamicObjectInterface) => {
    setData(prevUserData => ({
      ...prevUserData!,
      [name]: value,
    }));

    setClickedFields(prevClickedFields => ({
      ...prevClickedFields,
      [name]: value === '',
    }));

    if (name === 'email') {
      setEmailValid(isValidEmail(value as string));
    }

    if (name === 'phone' && value === '') {
      setPhoneValue('');
    }
  };

  return (
    <div>
      {/* {showHead && <AdminPageHeader title={t('admin.profile.editPersonalInfo')} button={buttonConfig} />} */}
      <div className={styles['card']}>
        <div className={styles['card-header']}>
          <h2>{t('admin.profile.editPersonalInfo')}</h2>
          {isDesktopView && (
            <div className={styles['edit-register-button']}>
              {buttonConfig.map(btn => (
                <Button key={btn.title} size={ButtonSize.SMALL} {...btn} />
              ))}
            </div>
          )}
        </div>
        <div className={styles['edit-personal-details']}>
          <div className={styles['form']}>
            <div className={styles['profile-container']}>
              <ProfileImage
                placeholder={data?.name ?? ''}
                src={profileImageUrl ? profileImageUrl : ProfilePhotoUpload}
              />
              <div className={styles['buttons-container']}>
                <button
                  className={styles['change-photo']}
                  onClick={() => document.getElementById('profileImageUrl')?.click()}
                >
                  <img src={ChangeButton} alt="bg" />
                  <input
                    type="file"
                    id="profileImageUrl"
                    accept="image/*"
                    onChange={e => handleFile(e, 'profileImageUrl')}
                    style={{ display: 'none' }}
                  />
                </button>
                <button className={styles['remove-photo']} onClick={() => deleteProfile()}>
                  <img src={RemoveButton} alt="bg" />
                </button>
              </div>
            </div>
            <div className={styles['input-container-three-column']}>
              <div className={styles['input-section']}>
                <Input
                  id="id"
                  disabled={true}
                  type={InputTypes.TEXT}
                  name={'name'}
                  placeholder=""
                  label={`${t('admin.userSetup.name')}`}
                  value={data?.name}
                  mlpVersion={MLPVersion.ONE}
                  onChange={e => {
                    handleInputChange('name', e.target.value);
                  }}
                  onClick={() =>
                    setClickedFields(prevClickedFields => ({ ...prevClickedFields, name: data?.name === '' }))
                  }
                  status={clickedFields.name ? InputValidations.INVALID : InputValidations.DEFAULT}
                  required={true}
                />
              </div>
              <div className={styles['input-section']}>
                <Input
                  disabled={true}
                  type={InputTypes.TEXT}
                  name={'loginId'}
                  placeholder=""
                  label={`${t('admin.userSetup.aboId')}`}
                  value={data?.loginId}
                  mlpVersion={MLPVersion.ONE}
                  status={clickedFields.loginId ? InputValidations.INVALID : InputValidations.DEFAULT}
                  required={true}
                />
              </div>
              <div className={styles['input-section']}>
                <div className={styles['input-label']}>
                  {`${t('admin.profile.contactNo')}`}
                  <span className={styles['label-red']}>*</span>
                </div>
                <PhoneNumber
                  name={'phoneNumber'}
                  data={countryCodeNumber}
                  inputValue={phoneValue}
                  dropdownValue={dropdownValue}
                  placeholder="Phone Number"
                  status={!phoneValid ? InputValidations.INVALID : InputValidations.DEFAULT}
                  //message={!phoneValid ? t('admin.userSetup.requiredPhone') : ''}
                  message={
                    phoneValue.length === 0 ? `Phone number is required` : !phoneValid ? `Invalid Phone Number` : ''
                  }
                  configData={{
                    label: 'name',
                    onSelect: (e: DropDownData) => {
                      setDropdownValue(e.name as string);
                      if (e.name) {
                        const combinedValue = `${e.name} ${phoneValue}`;
                        setPhoneValue(phoneValue === '' ? '' : phoneValue);
                        handleInputChange('phone', combinedValue);
                      } else {
                        setPhoneValue('');
                        handleInputChange('phone', '');
                      }
                    },
                  }}
                  onInputChange={val => {
                    const phoneNumber = val ?? '';
                    setPhoneValue(phoneNumber);
                    if (phoneNumber) {
                      const combinedValue = `${dropdownValue} ${phoneNumber}`;
                      setPhoneValue(phoneNumber === '' ? '' : phoneNumber);
                      handleInputChange('phone', combinedValue);
                    } else {
                      setPhoneValue('');
                      handleInputChange('phone', '');
                    }
                  }}
                  required={true}
                />
              </div>
            </div>
            <div className={styles['input-container-three-column']}>
              <div className={styles['input-section']}>
                <Input
                  key=""
                  //disabled={true}
                  type={InputTypes.EMAIL}
                  name={'email'}
                  placeholder="john_doe@domainname.com"
                  label={`${t('admin.userSetup.email')}`}
                  value={data?.email}
                  mlpVersion={MLPVersion.ONE}
                  onChange={e => {
                    handleInputChange('email', e.target.value);
                  }}
                  onClick={() =>
                    setClickedFields(prevClickedFields => ({ ...prevClickedFields, email: data?.email === '' }))
                  }
                  status={data?.email === '' || !emailValid ? InputValidations.INVALID : InputValidations.DEFAULT}
                  message={
                    data?.email === ''
                      ? t('admin.userSetup.requiredEmail')
                      : emailValid
                        ? ''
                        : t('admin.userSetup.invalidEmailFormat')
                  }
                  required={true}
                />
              </div>
              <div className={styles['input-section']}>
                <DropDown
                  name="gender"
                  dropdownLabel={`${t('admin.userSetup.gender')}`}
                  disabledFilter
                  status={data?.gender ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                  mlpVersion={MLPVersion.ONE}
                  value={data?.gender || ''}
                  data={[
                    { value: 'MALE', title: 'MALE' },
                    { value: 'FEMALE', title: 'FEMALE' },
                  ]}
                  configData={{
                    label: 'title',
                    onSelect: (selectedOption: DropDownData) => {
                      handleInputChange('gender', selectedOption.value);
                    },
                  }}
                  required={true}
                />
                {data?.gender === '' ? (
                  <div className={styles['text-error-message']}>{t('admin.userSetup.requiredGender')}</div>
                ) : null}
              </div>
              <div className={styles['input-section']}>
                <DropDown
                  name="race"
                  dropdownLabel={`${t('admin.profile.race')}`}
                  disabledFilter
                  status={data?.race ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                  mlpVersion={MLPVersion.ONE}
                  value={data?.race || ''}
                  data={[
                    { value: 'CHINESE', title: 'CHINESE' },
                    { value: 'MALAY', title: 'MALAY' },
                    { value: 'INDIAN', title: 'INDIAN' },
                    { value: 'OTHER', title: 'OTHER' },
                  ]}
                  configData={{
                    label: 'title',
                    onSelect: (selectedOption: DropDownData) => {
                      handleInputChange('race', selectedOption.value);
                    },
                  }}
                  required={true}
                />
                {data?.race === '' ? (
                  <div className={styles['text-error-message']}>{t('admin.userSetup.requiredGender')}</div>
                ) : null}
              </div>
            </div>
            <div className={styles['input-container']}>
              <div className={styles['input-section']}>
                <Input
                  key="id"
                  type={InputTypes.TEXT}
                  name={'address'}
                  placeholder=""
                  label={`${t('admin.profile.address')}`}
                  value={data?.address}
                  mlpVersion={MLPVersion.ONE}
                  onChange={e => {
                    handleInputChange('address', e.target.value);
                  }}
                  onClick={() =>
                    setClickedFields(prevClickedFields => ({ ...prevClickedFields, address: data?.address === '' }))
                  }
                  status={clickedFields.address ? InputValidations.INVALID : InputValidations.DEFAULT}
                />
              </div>
              {/* <div className={styles['input-section']}>
                <Input
                  key="id"
                  type={InputTypes.TEXT}
                  name={'state'}
                  placeholder=""
                  label={`${t('admin.profile.state')}*`}
                  value={data?.state}
                  mlpVersion={MLPVersion.ONE}
                  onChange={e => {
                    handleInputChange('state', e.target.value);
                  }}
                  onClick={() =>
                    setClickedFields(prevClickedFields => ({ ...prevClickedFields, state: data?.state === '' }))
                  }
                  status={clickedFields.state ? InputValidations.INVALID : InputValidations.DEFAULT}
                />
              </div> */}
            </div>
            <div className={styles['input-container-three-column']}>
              <div className={styles['input-section']}>
                <DropDown
                  name="occupation"
                  dropdownLabel={`${t('admin.profile.occupation')}`}
                  disabledFilter
                  status={data?.occupation ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                  mlpVersion={MLPVersion.ONE}
                  isWithinModal={false}
                  value={data?.occupation || ''}
                  data={[
                    { value: 'Full Time ABO', title: 'Full Time ABO' },
                    { value: 'Business and Sales', title: 'Business and Sales' },
                    { value: 'Education and Training', title: 'Education and Training' },
                    { value: 'Healthcare', title: 'Healthcare' },
                    { value: 'Engineering and IT', title: 'Engineering and IT' },
                    { value: 'Legal and Finance', title: 'Legal and Finance' },
                    { value: 'Marketing and Customer Service', title: 'Marketing and Customer Service' },
                    { value: 'Administration and Support', title: 'Administration and Support' },
                    { value: 'Other', title: 'Other' },
                  ]}
                  configData={{
                    label: 'title',
                    onSelect: (selectedOption: DropDownData) => {
                      handleInputChange('occupation', selectedOption.value);
                    },
                  }}
                />
                {data?.occupation === '' ? (
                  <div className={styles['text-error-message']}>{t('admin.userSetup.requiredGender')}</div>
                ) : null}
              </div>
              <div className={styles['input-section']}>
                <DropDown
                  name="incomeRange"
                  dropdownLabel={`${t('admin.profile.incomeRange')}`}
                  disabledFilter
                  status={data?.incomeRange ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                  mlpVersion={MLPVersion.ONE}
                  value={data?.incomeRange || ''}
                  data={[
                    { value: 'Below SGD3,500', title: 'Below SGD3,500' },
                    { value: 'SGD3,501 - SGD8,000', title: 'SGD3,501 - SGD8,000' },
                    { value: 'SGD8,001 - SGD11,000', title: 'SGD8,001 - SGD11,000' },
                    { value: 'Above SGD11,000', title: 'Above SGD11,000' },
                  ]}
                  configData={{
                    label: 'title',
                    onSelect: (selectedOption: DropDownData) => {
                      handleInputChange('incomeRange', selectedOption.value);
                    },
                  }}
                />
                {data?.incomeRange === '' ? (
                  <div className={styles['text-error-message']}>{t('admin.userSetup.requiredGender')}</div>
                ) : null}
              </div>
            </div>
            {!isDesktopView && (
              <div className={styles['edit-register-button']}>
                {buttonConfig.map(btn => (
                  <Button key={btn.title} size={ButtonSize.SMALL} {...btn} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {uploadImageModal}
    </div>
  );
};
